import { DropdownMenu as Primitive } from "radix-ui";
import type { ReactNode } from "react";
import { useMenuModal, usePortalContainer, useStageBoundary } from "../portalContainer.tsx";
import { cn } from "../../cn.ts";
import { menuPanelClassName, renderItems, type MenuFlavour, type MenuItem } from "./menuItems.tsx";

export type { MenuItem } from "./menuItems.tsx";

/**
 * FR-015-101, § Overlays, DropdownMenu, taken from shadcn/ui's Dropdown Menu over Radix and
 * themed through the variables of `packages/tokens` (2026-09-15, by the owner's decision): the
 * item shape, typeahead (Radix's built-in), submenus, checkbox and radio items, closing on outside
 * interaction and `Esc`. Spectrum UI's Profile Dropdown is the shell's account menu, not this
 * component. `onCloseAutoFocus` is Radix's own hook for a menu whose item opens another surface:
 * that surface calls `preventDefault` so closing the menu does not return focus to its trigger,
 * which would dismiss the surface as a focus outside it. Inside a popover the menu is not modal
 * (`useMenuModal`, 2026-10-02), so a press outside closes the popover with it and the popover stays
 * open through the menu's life.
 *
 * What differs from the source, and why: the panel is as wide as its longest item between
 * `--menu-width-min` and `--menu-width-max`, and never wider than the space Radix measures beside
 * the trigger, where the source sets only an 8 rem minimum (§ Component sizes); the rows are
 * `menuItems.tsx`'s.
 */
export interface DropdownMenuProps {
  readonly trigger: ReactNode;
  readonly items: readonly MenuItem[];
  readonly align?: "start" | "center" | "end";
  /** The trigger's side the menu opens on, below it unless given: a rail's menu opens to its right. */
  readonly side?: "top" | "right" | "bottom" | "left";
  readonly label?: string;
  readonly open?: boolean;
  readonly onOpenChange?: (open: boolean) => void;
  readonly onCloseAutoFocus?: (event: Event) => void;
}

/** A submenu's portal, into the dialog the menu opened in when there is one (`portalContainer.tsx`). */
function MenuPortal({ children }: { readonly children?: ReactNode }) {
  const container = usePortalContainer();
  return <Primitive.Portal {...(container === undefined ? {} : { container })}>{children}</Primitive.Portal>;
}

const flavour: MenuFlavour = {
  parts: {
    Item: Primitive.Item,
    CheckboxItem: Primitive.CheckboxItem,
    RadioGroup: Primitive.RadioGroup,
    RadioItem: Primitive.RadioItem,
    ItemIndicator: Primitive.ItemIndicator,
    Separator: Primitive.Separator,
    Label: Primitive.Label,
    Sub: Primitive.Sub,
    SubTrigger: Primitive.SubTrigger,
    SubContent: Primitive.SubContent,
    Portal: MenuPortal,
  },
  slot: "dropdown-menu",
  subContentClassName: cn(menuPanelClassName, "max-w-(--menu-width-max) origin-(--radix-dropdown-menu-content-transform-origin) overflow-hidden shadow-lg"),
};

const contentClassName = cn(
  menuPanelClassName,
  "max-h-(--radix-dropdown-menu-content-available-height) max-w-[min(var(--menu-width-max),var(--radix-dropdown-menu-content-available-width))] origin-(--radix-dropdown-menu-content-transform-origin) overflow-x-hidden overflow-y-auto shadow-md",
);

export function DropdownMenu({ trigger, items, align = "start", side = "bottom", label, open, onOpenChange, onCloseAutoFocus }: DropdownMenuProps) {
  const boundary = useStageBoundary();
  const modal = useMenuModal();
  return (
    <Primitive.Root data-slot="dropdown-menu" modal={modal} {...(open === undefined ? {} : { open })} {...(onOpenChange === undefined ? {} : { onOpenChange })}>
      <Primitive.Trigger data-slot="dropdown-menu-trigger" asChild>
        {trigger}
      </Primitive.Trigger>
      <MenuPortal>
        <Primitive.Content data-slot="dropdown-menu-content" className={contentClassName} {...boundary} side={side} align={align} sideOffset={4} collisionPadding={8} {...(label === undefined ? {} : { "aria-label": label })} {...(onCloseAutoFocus === undefined ? {} : { onCloseAutoFocus })}>
          {renderItems(items, flavour)}
        </Primitive.Content>
      </MenuPortal>
    </Primitive.Root>
  );
}
